import { OrderDateRange, OrderHistoryParams } from '@diamond/shared/types';
import { unixToHumanDate } from '@diamond/shared/utils';
import { getUnixTime, subDays } from 'date-fns';
import { useState } from 'react';

const DAYS_MAP: Record<Exclude<OrderDateRange, 'all' | 'custom'>, number> = {
  '7d': 7,
  '1m': 30,
  '3m': 30 * 3,
  '6m': 30 * 6,
  '1y': 365,
};

export type GetMyOrderFiltersReturnType = ReturnType<typeof useMyOrderFilters>;

export function useMyOrderFilters(
  query: Record<string, string | string[] | undefined>,
  defaultRange?: OrderDateRange
) {
  const [today] = useState(() => new Date());
  // Used for filtering products
  const status = query?.['status'] as OrderHistoryParams['status'];
  const divisi_code = query?.[
    'divisi_code'
  ] as OrderHistoryParams['divisi_code'];

  const isPredefinedRange = defaultRange !== 'all' && defaultRange !== 'custom';

  const startDateUnix = getUnixTime(
    subDays(
      today,
      defaultRange && isPredefinedRange
        ? DAYS_MAP[defaultRange]
        : DAYS_MAP['7d']
    )
  );
  const endDateUnix = getUnixTime(today);

  // 7d, 1m. 3m, 6m
  const date_range = (() => {
    const value = query?.['date_range'] as string | undefined;
    if (value) return value;
    if (
      query?.['start_date'] !== undefined ||
      query?.['end_date'] !== undefined
    )
      return 'custom';
    return defaultRange;
  })() as OrderDateRange;

  const start_date_str = (() => {
    const value = query?.['start_date'] as string | undefined;
    if (value) return value;
    if (date_range === 'all') return '';
    return startDateUnix;
  })();

  const end_date_str = (() => {
    const value = query?.['end_date'] as string | undefined;
    if (value) return value;
    if (date_range === 'all') return '';
    return endDateUnix;
  })();

  const start_date = !isNaN(+start_date_str) ? +start_date_str : 0;
  const end_date = !isNaN(+end_date_str) ? +end_date_str : 0;

  const search = (query?.['search'] as string)?.trim() ?? '';
  const group_status = query?.[
    'group_status'
  ] as OrderHistoryParams['group_status'];

  const page = +(query?.['page'] as string) || 1;

  return {
    status,
    divisi_code,
    start_date,
    end_date,
    start_date_str,
    end_date_str,
    'order_date-from': start_date
      ? unixToHumanDate(start_date, 'dd/MM/yyyy')
      : '',
    'order_date-to': end_date ? unixToHumanDate(end_date, 'dd/MM/yyyy') : '',
    search,
    group_status,
    page,
    date_range,
  };
}
